import { Injectable } from '@angular/core'
import { Apollo, gql } from 'apollo-angular'
import { Observable } from 'rxjs'
import { MessageService } from 'primeng/api'
import { AuthService } from '@app/core/auth/services/auth.service'
import { TranslateService } from '@ngx-translate/core'
import type { DocumentNode } from 'graphql'
import { ExpiringRequirement, REQUIREMENTS_EXPIRING, buildRequirementsExpiringMessage } from '@app/utils/requirements-expiring.util'

type ErrorType = { success: false; error: unknown }

type GraphQLErrorLike = { message: string }

/** Envelope shapes we navigate on Apollo results; the server contract is dynamic per operation */
type ResultEnvelope = {
  errors?: GraphQLErrorLike[]
  error?: { errors?: GraphQLErrorLike[] }
  data?: unknown
} | null | undefined

type ApolloErrorLike = {
  graphQLErrors?: GraphQLErrorLike[]
  networkError?: { error?: { errors?: GraphQLErrorLike[] } } | null
}

export type ApiVariables = Record<string, unknown>
// never-param on purpose: legacy callers annotate the callback param as their own
// input type even though it receives the base variables ({ client_id }) at runtime;
// contravariance makes every such callback assignable. _wrap casts at the call.
export type ApiVarsFn = (base: never) => ApiVariables
export type ApiVars = ApiVariables | ApiVarsFn

@Injectable({ providedIn: 'root' })
export class ApiService {
  constructor(private apollo: Apollo, private authService: AuthService, private messageService: MessageService, private translate: TranslateService) {}
  /**
   * `silentErrorTexts` lists refusal codes this caller handles itself, so the automatic error
   * toast is skipped for them and only for them — every other failure still surfaces. It exists
   * for refusals that are really a QUESTION rather than an error (e.g. PAPI's
   * `booking_allowance_exceeded`, which the portal answers with a confirm-and-retry): showing a
   * red toast and then a confirmation dialog for the same event reads as a bug.
   */
  _wrap<T>(method: 'query' | 'mutate', query: string | DocumentNode, vars: ApiVars, errorTextTranslateKey?: string, silentErrorTexts?: readonly string[]) {
    const doc: DocumentNode = 'string' === typeof query ? gql(query) : query
    return new Observable<T | ErrorType>((observer) => {
      const dataHandler = (data: unknown): unknown => {
        // Handle errors - check both data.errors and data.error.errors
        const envelope = data as ResultEnvelope
        const errors = envelope?.errors || envelope?.error?.errors
        if (errors?.length) {
          // Consolidate multiple errors into a single message
          if (errors.length === 1) {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('SYSTEM.ERROR'),
              detail: errors[0].message,
              life: 10000,
              styleClass: 'multiline-message',
            })
          } else {
            // Multiple errors - show consolidated message
            const errorDetails = errors.map((e, idx) => `${idx + 1}. ${e.message}`).join('\n')
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('SYSTEM.ERROR'),
              detail: errorDetails,
              life: 15000,
              styleClass: 'multiline-message',
            })
          }
          // Return error response when GraphQL errors exist
          return {
            success: false,
            errorText: errors[0].message,
            errors: errors,
          }
        }
        let response: unknown = data

        if (envelope?.data) {
          response = envelope.data
        }

        const withToken = response as { access_token?: string } | null | undefined
        if (withToken?.access_token) {
          this.authService.setToken(withToken.access_token)
        }

        if (response && typeof response === 'object') {
          const record = response as Record<string, { success?: boolean; errorText?: string; errorCode?: string | null; expiring_requirements?: ExpiringRequirement[] | null } | undefined>
          for (const key in record) {
            const entry = record[key]
            /* YB-2159: a pass refused because its role's requirements expire during it. The message
               is built from the code and the details, never from errorText (PAPI's English), and it
               stays until closed: it lists what to renew and the user acts on it. A caller that shows
               the details itself silences it by listing the code in silentErrorTexts. */
            if (!entry?.success && entry?.errorCode === REQUIREMENTS_EXPIRING && entry.expiring_requirements?.length) {
              if (!silentErrorTexts?.includes(REQUIREMENTS_EXPIRING)) {
                this.messageService.add({
                  severity: 'error',
                  ...buildRequirementsExpiringMessage(this.translate, entry.expiring_requirements),
                  sticky: true,
                  styleClass: 'multiline-message',
                })
              }
              continue
            }
            if (!entry?.success && entry?.errorText && !silentErrorTexts?.includes(entry.errorText)) {
              this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('SYSTEM.ERROR'),
                detail: errorTextTranslateKey ? this.translate.instant(errorTextTranslateKey + entry.errorText) : entry.errorText,
                life: 10000,
                styleClass: 'multiline-message',
              })
            }
          }
          const keys = Object.keys(record).filter((el: string) => el.indexOf('access_token') == -1)

          if (keys.length === 1) {
            response = record[keys[0]]
          }
        }
        return response
      }
      const errorHandler = (error: unknown) => {
        const err = error as ApolloErrorLike
        if (err.graphQLErrors?.length) {
          // Consolidate multiple GraphQL errors into a single message
          if (err.graphQLErrors.length === 1) {
            this.messageService.add({
              severity: 'error',
              summary: 'GraphQL Error',
              detail: err.graphQLErrors[0].message,
              life: 10000,
              styleClass: 'multiline-message',
            })
          } else {
            const errorDetails = err.graphQLErrors.map((e, idx) => `${idx + 1}. ${e.message}`).join('\n')
            this.messageService.add({
              severity: 'error',
              summary: `${err.graphQLErrors.length} GraphQL Errors`,
              detail: errorDetails,
              life: 15000,
              styleClass: 'multiline-message',
            })
          }
        }
        if (err.networkError) {
          const networkErrors = err.networkError.error?.errors
          if (networkErrors?.length) {
            // Consolidate multiple network errors into a single message
            if (networkErrors.length === 1) {
              this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('SYSTEM.NETWORK_ERROR'),
                detail: networkErrors[0].message,
                life: 10000,
                styleClass: 'multiline-message',
              })
            } else {
              const errorDetails = networkErrors.map((e, idx) => `${idx + 1}. ${e.message}`).join('\n')
              this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('SYSTEM.NETWORK_ERROR'),
                detail: errorDetails,
                life: 15000,
                styleClass: 'multiline-message',
              })
            }
          }
        }
        complete({
          success: false,
          error,
        })
      }
      const complete = (data: unknown) => {
        observer.next(JSON.parse(JSON.stringify(data)) as T | ErrorType)
        observer.complete()
      }
      this.authService
        .getUser()
        .then((user) => {
          let variables: ApiVariables = {
            client_id: user ? Number(user.client_id) : null,
          }
          if ('function' === typeof vars) {
            variables = (vars as (base: ApiVariables) => ApiVariables)(variables)
          } else {
            variables = Object.assign(variables, vars)
          }
          const baseOptions = {
            variables,
            fetchPolicy: 'no-cache' as const,
            errorPolicy: 'all' as const,
          }
          if (method === 'query') {
            this.apollo.query({ ...baseOptions, query: doc }).subscribe({
              next: (result) => {
                complete(dataHandler(result))
              },
              error: errorHandler,
            })
          } else if (method === 'mutate') {
            this.apollo.mutate({ ...baseOptions, mutation: doc }).subscribe({
              next: (result) => {
                complete(dataHandler(result))
              },
              error: errorHandler,
            })
          }
        })
        .catch((e: Error) => {
          observer.next({
            success: false,
            error: e,
          })
          observer.complete()
        })
    })
  }
  privateQuery<T>(query: string | DocumentNode, vars?: ApiVars, errorTextTranslateKey?: string) {
    return this._wrap<T>('query', query, vars || {}, errorTextTranslateKey)
  }
  privateMutation<T>(query: string | DocumentNode, vars?: ApiVars, errorTextTranslateKey?: string, silentErrorTexts?: readonly string[]) {
    return this._wrap<T>('mutate', query, vars || {}, errorTextTranslateKey, silentErrorTexts)
  }
}
